import { Heart, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { EmptyState } from '../components/EmptyState';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Panel } from '../components/ui/panel';
import { Skeleton } from '../components/ui/skeleton';
import { useDeleteWishlistItem, useWishlist } from '../features/wishlist/queries';
import { imageUrl } from '../lib/api';
import { coverGradient } from '../lib/cover';

const PRIORITY_VARIANTS = ['accent', 'neutral', 'outline'] as const;

export function WishlistPage() {
  const { t } = useTranslation();
  const wishlist = useWishlist();
  const removeItem = useDeleteWishlistItem();

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-3xl text-foreground">{t('nav.wishlist')}</h1>
        <p className="mt-1 text-sm text-muted">
          {t('wishlist.count', { count: wishlist.data?.length ?? 0 })}
        </p>
      </header>

      {wishlist.isPending ? (
        <Skeleton className="h-40" />
      ) : (wishlist.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Heart}
          title={t('wishlist.emptyTitle')}
          description={t('wishlist.emptyBody')}
        />
      ) : (
        <Panel className="divide-y">
          {wishlist.data?.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <Link
                to={entry.targetSeriesId ? `/series/${entry.targetSeriesId}` : '/wishlist'}
                className="group flex min-w-0 items-center gap-3"
              >
                <span
                  className="aspect-[2/3] w-10 shrink-0 overflow-hidden rounded border bg-surface-raised"
                  style={
                    entry.coverImageId
                      ? undefined
                      : { backgroundImage: coverGradient(entry.seriesTitle ?? '?') }
                  }
                >
                  {entry.coverImageId ? (
                    <img
                      src={imageUrl(entry.coverImageId, 'thumb')}
                      alt=""
                      loading="lazy"
                      className="size-full object-cover"
                    />
                  ) : null}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-foreground group-hover:text-accent">
                    {entry.seriesTitle ?? t('wishlist.unknownSeries')}
                    {entry.issueNumber ? (
                      <span className="font-display text-muted"> #{entry.issueNumber}</span>
                    ) : null}
                  </span>
                  {entry.notes ? (
                    <span className="block truncate text-xs text-muted">{entry.notes}</span>
                  ) : null}
                </span>
              </Link>

              <div className="flex items-center gap-2">
                <Badge variant={PRIORITY_VARIANTS[entry.priority - 1] ?? 'neutral'}>
                  {t(`wishlist.priority${entry.priority}`)}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('wishlist.remove')}
                  disabled={removeItem.isPending}
                  onClick={() => void removeItem.mutateAsync(entry.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}
